import React, { useImperativeHandle, forwardRef } from 'react';
import { GoogleMapViewProps, GoogleMapViewHandle } from './types';
import { useMapInit } from './useMapInit';
import { useMarkers } from './useMarkers';
import { usePulseOverlay } from './usePulseOverlay';
import { useCrimeHeatmap } from '../../pages/opportunityByRegion/useCrimeHeatmap';

/**
 * Reusable Google Maps component.
 *
 * Renders a Google Map with markers for the supplied stores. All advanced
 * features (clustering, region view, neighbourhood, export, etc.) are opt-in
 * via props.
 */
const GoogleMapView = forwardRef<GoogleMapViewHandle, GoogleMapViewProps>(
  (
    {
      stores,
      center,
      zoom = 4,
      fitBounds = false,
      height = '100%',
      mapTypeControl = true,
      streetViewControl = false,
      showDetailedMarkers = true,
      zoomThreshold = 13,
      showHeatmap = false,
      referenceStoreId,
      exportSelection,
      geocodeAddress,
      infoWindowOptions,
      onMapReady,
      onMarkerClick,
      onStreetViewClick,
      onZoomChange,
    },
    ref
  ) => {
    // 1. Initialise the map
    const { containerRef, map, loading, error } = useMapInit({
      center,
      zoom,
      mapTypeControl,
      streetViewControl,
      geocodeAddress,
      onMapReady,
      onZoomChange,
    });

    // 2. Manage markers
    const { markersRef, currentInfoWindowRef } = useMarkers({
      map,
      stores,
      referenceStoreId,
      showDetailedMarkers,
      zoomThreshold,
      fitBounds,
      exportSelection,
      infoWindowOverrides: infoWindowOptions,
      onStreetViewClick,
      onMarkerClick,
    });

    // 3. Reference store pulse overlay
    usePulseOverlay(map, stores, referenceStoreId);

    // 4. Crime heatmap
    useCrimeHeatmap(map, showHeatmap);

    // 5. Expose imperative methods to parent
    useImperativeHandle(ref, () => ({
      getMap: () => map,
      openInfoWindow: (storeId: string) => {
        const entry = markersRef.current.get(storeId);
        if (!entry || !map) return;
        if (currentInfoWindowRef.current) currentInfoWindowRef.current.close();
        entry.infoWindow.open(map, entry.marker);
        entry.marker.setZIndex(google.maps.Marker.MAX_ZINDEX + 1);
        currentInfoWindowRef.current = entry.infoWindow;
      },
      panToStore: (storeId: string, zoomLevel?: number) => {
        const entry = markersRef.current.get(storeId);
        if (!entry || !map) return;
        if (zoomLevel) map.setZoom(zoomLevel);
        map.panTo(entry.marker.getPosition()!);
      },
    }));

    return (
      <div style={{ position: 'relative', width: '100%', height }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#f3f4f6',
              zIndex: 1,
            }}
          >
            <span>Loading map...</span>
          </div>
        )}
        {error && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: '#fef2f2',
              color: '#dc2626',
              zIndex: 1,
            }}
          >
            {error}
          </div>
        )}
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      </div>
    );
  }
);

GoogleMapView.displayName = 'GoogleMapView';

export default React.memo(GoogleMapView);
